import { ReactNode } from "react"
import clsx from "clsx"
import { FaXTwitter } from "react-icons/fa6"
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import Button from "@/components/Button"
import { Link } from "@inertiajs/react"
import Container from "@/components/Container"
import useUser from "@/hooks/useUser"
import usePathname from "@/hooks/usePathname"
import SEO from "@/components/SEO"
import PromotionsBanner from "@/components/PromotionsBanner"
import Logo from "@/components/Logo"
import Toaster from "@/components/Toaster"

const additionalLinks = [
  {
    label: "Terms & Conditions",
    href: "/terms",
  },
  {
    label: "Privacy Policy",
    href: "/privacy",
  },
]

const DefaultLayout = ({
  children,
  headerClassName,
}: {
  children: ReactNode
  promotions?: any[]
  headerClassName?: string
}) => {
  const { isAuthenticated } = useUser()
  const pathname = usePathname()
  const loginHref = `/login?return_to=${encodeURIComponent(pathname)}`

  return (
    <>
      <SEO />

      <PromotionsBanner />

      <header className={clsx(headerClassName)}>
        <Container>
          <nav
            className="flex items-center justify-between py-4 sm:py-5"
            aria-label="Global"
          >
            <div className="flex items-center gap-x-4">
              <Link
                href="/"
                className="-m-2.5 rounded-md p-2.5 focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:outline-none"
              >
                <span className="sr-only">ChessDrop</span>
                <Logo />
              </Link>
            </div>

            <Popover className="sm:hidden">
              <PopoverButton className="-m-2.5 rounded-md p-2.5 text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:outline-none">
                <span className="sr-only">Open menu</span>
                <Bars3Icon aria-hidden="true" className="size-6" />
              </PopoverButton>

              <PopoverPanel
                transition
                className="absolute inset-x-0 top-0 z-50 border-b border-gray-900/10 bg-white shadow-xl data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-100 data-leave:ease-in"
              >
                {({ close }) => (
                  <>
                    <Container className="flex items-center justify-between pt-4 sm:pt-5">
                      <CloseButton
                        as={Link}
                        href="/"
                        className="-m-2.5 rounded-md p-2.5 focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:outline-none"
                      >
                        <span className="sr-only">ChessDrop</span>
                        <Logo />
                      </CloseButton>
                      <PopoverButton className="-m-2.5 rounded-md p-2.5 text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:outline-none">
                        <span className="sr-only">Close menu</span>
                        <XMarkIcon aria-hidden="true" className="size-6" />
                      </PopoverButton>
                    </Container>
                    <Container className="py-5">
                      {isAuthenticated ? (
                        <Button
                          href="/games"
                          style="primary"
                          size="md"
                          onClick={() => close()}
                          fullWidth
                        >
                          Dashboard
                        </Button>
                      ) : (
                        <div className="grid grid-cols-3 gap-x-2">
                          <Button
                            href={loginHref}
                            onClick={() => close()}
                            style="secondary"
                            size="md"
                          >
                            Log in
                          </Button>
                          <Button
                            href="/signup"
                            onClick={() => close()}
                            style="secondary"
                            size="md"
                          >
                            Sign up
                          </Button>
                          <Button
                            href="/try"
                            onClick={() => close()}
                            style="primary"
                            size="md"
                          >
                            Try it!
                          </Button>
                        </div>
                      )}
                    </Container>
                  </>
                )}
              </PopoverPanel>
            </Popover>

            <div className="hidden sm:flex sm:items-center sm:gap-x-2">
              {isAuthenticated ? (
                <Button href="/games" style="primary">
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button href={loginHref} style="secondary">
                    Log in
                  </Button>
                  <Button href="/signup" style="secondary">
                    Sign up
                  </Button>
                  <Button href="/try" style="primary">
                    Try it!
                  </Button>
                </>
              )}
            </div>
          </nav>
        </Container>
      </header>

      <main className="flex flex-1 flex-col">{children}</main>

      <Container tag="footer" className="py-8 sm:py-12">
        <div className="flex items-center justify-between pb-4 sm:pb-8">
          <Logo />
          <div>
            <a
              href="https://x.com/ChessDrop"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-900"
            >
              <span className="sr-only">Twitter</span>
              <FaXTwitter className="size-6" />
            </a>
          </div>
        </div>
        <div className="flex flex-col-reverse items-center justify-between gap-y-4 border-t border-gray-200 pt-4 sm:flex-row sm:gap-y-8 sm:pt-8">
          <div className="text-medium text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Lakpa SLU
          </div>
          <div className="flex gap-x-8">
            {additionalLinks.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                className="text-medium text-sm text-gray-600 hover:underline"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </Container>

      <Toaster />
    </>
  )
}

export default DefaultLayout
